import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { fetchAllHealth, fetchAllDashboards, fetchRealProjects } from '../services/api'
import Sidebar from '../components/Sidebar'
import MobileNav from '../components/MobileNav'
import HealthBar from '../components/HealthBar'
import AlertBanner from '../components/AlertBanner'
import AnimatedNumber from '../components/AnimatedNumber'
import GlowingTrackBar from '../components/GlowingTrackBar'
import SkeletonCard, { SkeletonHealthBar } from '../components/SkeletonCard'
import CostPanel from '../components/CostPanel'
import RealTimeProjectStatus from '../components/RealTimeProjectStatus'
import MuwafaqECCControls from '../components/MuwafaqECCControls'
import EnhancedAnalytics from '../components/EnhancedAnalytics'
import ExecutiveOverview from '../components/ExecutiveOverview'
import { milestones, products as productConfigs, getMilestoneStats, getOverallStats, getDaysRemainingLabel, daysRemaining } from '../data/milestones'
import { safeEntries } from '../utils/safe'

// Company info
const COMPANY = {
  name: 'Sanajim Technologies',
  nameAr: 'سناجم تكنولوجيا',
  tagline: 'AI-Agent-Only SaaS Company — KSA',
  taglineAr: 'شركة SaaS تعمل بالكامل بالوكلاء الذكيين — المملكة العربية السعودية',
  founded: '2026',
  employees: 0, // AI-only
  agents: 7,
}

// Product definitions with descriptions
const PRODUCTS = {
  rafiq: {
    icon: '🚗',
    name: 'Rafiq',
    nameAr: 'رفيق',
    description: 'Driver companion app for Saudi transport companies',
    descriptionAr: 'تطبيق رفيق للسائقين — شركات النقل السعودية',
    color: '#00BFA5',
    light: '#00E5CC',
    metrics: [
      { key: 'activeMissions', label: 'مهام نشطة', labelEn: 'Active Missions', icon: '🟢' },
      { key: 'totalMissions', label: 'إجمالي المهام', labelEn: 'Total Missions', icon: '📋' },
      { key: 'driverCount', label: 'سائقين', labelEn: 'Drivers', icon: '👤' },
      { key: 'employerCount', label: 'جهات', labelEn: 'Employers', icon: '🏢' },
    ],
    url: 'https://app.sanajim.com',
    status: 'Beta — V0.3 APK delivered',
    statusAr: 'بيتا — الإصدار 0.3 جاهز',
  },
  muwafaq: {
    icon: '📋',
    name: 'Muwafaq',
    nameAr: 'موافق',
    description: 'NCA ECC-2:2024 compliance readiness platform',
    descriptionAr: 'منصة موافقة لجاهزية الامتثال — الهيئة الوطنية للأمن السيبراني',
    color: '#4CAF50',
    light: '#81C784',
    metrics: [
      { key: 'complianceScore', label: 'نسبة الامتثال', labelEn: 'Compliance %', icon: '📊', format: 'percent' },
      { key: 'totalControls', label: 'إجمالي الضوابط', labelEn: 'Total Controls', icon: '🛡️' },
      { key: 'verifiedControls', label: 'ضوابط محققة', labelEn: 'Verified', icon: '✅' },
      { key: 'criticalFindings', label: 'نتائج حرجة', labelEn: 'Critical', icon: '🔴', format: 'length' },
    ],
    url: 'https://muwafaq.sanajim.com',
    status: 'Production — Live',
    statusAr: 'إنتاج — يعمل',
  },
  sahha: {
    icon: '🏥',
    name: 'Sahha',
    nameAr: 'صحة',
    description: 'Healthcare claims AI operations co-pilot',
    descriptionAr: 'طيار عمليات المطالبات الصحية بالذكاء الاصطناعي',
    color: '#448AFF',
    light: '#82B1FF',
    metrics: [
      { key: 'totalClaims', label: 'إجمالي المطالبات', labelEn: 'Total Claims', icon: '📋' },
      { key: 'approvedClaims', label: 'مطالبات مقبولة', labelEn: 'Approved', icon: '✅' },
      { key: 'rejectedClaims', label: 'مطالبات مرفوضة', labelEn: 'Rejected', icon: '❌' },
      { key: 'approvalRate', label: 'نسبة القبول', labelEn: 'Approval Rate', icon: '📈', format: 'percent' },
    ],
    url: 'https://sahha.sanajim.com',
    status: 'Beta — Testing',
    statusAr: 'بيتا — اختبار',
  },
  labor: {
    icon: '👷',
    name: 'Labor Portal',
    nameAr: 'بوابة العمالة',
    description: 'B2B labor allocation marketplace for KSA',
    descriptionAr: 'سوق تخصيص العمالة بين المؤسسات — المملكة العربية السعودية',
    color: '#FF6B35',
    light: '#FF9E6D',
    metrics: [
      { key: 'totalCompanies', label: 'شركات مسجلة', labelEn: 'Companies', icon: '🏢' },
      { key: 'activeOffers', label: 'عروض نشطة', labelEn: 'Active Offers', icon: '📋' },
      { key: 'activeRequests', label: 'طلبات نشطة', labelEn: 'Active Requests', icon: '📝' },
      { key: 'completedContracts', label: 'عقود منجزة', labelEn: 'Contracts', icon: '✅' },
    ],
    url: '',
    status: 'Development — Building',
    statusAr: 'تطوير — قيد البناء',
  },
  aiRacingGame: {
    icon: '🏎️',
    name: 'AI Racing Game',
    nameAr: 'لعبة سباق بالذكاء الاصطناعي',
    description: 'Wreckfest-inspired mobile racing game with AI bots',
    descriptionAr: 'لعبة سباق على الهاتف مستوحاة من Wreckfest مع خصوم ذكاء اصطناعي',
    color: '#FF4081',
    light: '#FF79B0',
    metrics: [
      { key: 'fpsPerformance', label: 'أداء الإطارات', labelEn: 'FPS Performance', icon: '⚡', format: 'percent' },
      { key: 'aiBotCount', label: 'عدد خصوم الذكاء الاصطناعي', labelEn: 'AI Bots', icon: '🤖' },
      { key: 'gameModes', label: 'أنماط اللعبة', labelEn: 'Game Modes', icon: '🎮' },
      { key: 'developmentProgress', label: 'تقدم التطوير', labelEn: 'Dev Progress', icon: '📈', format: 'percent' },
    ],
    url: '',
    status: 'Planning — AI-Driven',
    statusAr: 'تخطيط — يعمل بالذكاء الاصطناعي',
  },
}

// Strategic Projects data
const STRATEGIC_PROJECTS = [
  {
    id: 'rafiq',
    icon: '🚗',
    name: 'Rafiq',
    nameAr: 'رفيق',
    subtitle: 'Driver Companion App',
    subtitleAr: 'تطبيق رفيق للسائقين',
    status: 'live',
    statusLabel: 'v1.0.0 — منتج',
    progress: milestones[0] ? getMilestoneStats(milestones[0]).avgProgress : 0,
    color: '#00BFA5',
    light: '#00E5CC',
    target: 'Q2 2026',
    tech: 'Flutter + Node.js + SQLite',
    pricing: '٧٩ / ١٢٩ / ٢٤٩ ريال/شهر',
    milestones: [
      { version: 'V1.0', status: 'done', items: ['تسجيل الدخول', 'إنشاء المهام', 'تحليل AI (DeepSeek V3)', 'لوحة التحكم', 'إدارة السائقين'] },
      { version: 'V2.0', status: 'active', items: ['خرائط Google', 'تسجيل صوتي', 'واجهة السائق', 'لوحة صاحب العمل', 'تتبع GPS مباشر'] },
      { version: 'V3.0', status: 'planned', items: ['إشعارات فورية', 'وضع بدون إنترنت', 'دعم متعدد اللغات', 'لوحة تحليلات'] },
    ],
  },
  {
    id: 'muwafaq',
    icon: '📋',
    name: 'Muwafaq',
    nameAr: 'موافق',
    subtitle: 'NCA Compliance Platform',
    subtitleAr: 'منصة جاهزية الامتثال — NCA',
    status: 'live',
    statusLabel: 'يعمل على الإنتاج',
    progress: milestones[1] ? getMilestoneStats(milestones[1]).avgProgress : 0,
    color: '#4CAF50',
    light: '#81C784',
    target: 'Q3 2026',
    tech: 'Node.js + SQLite + React',
    pricing: '٤٩٩ / ٩٩٩ / ٢,٤٩٩ ريال/شهر',
    milestones: [
      { version: 'V1.0', status: 'done', items: ['١١١ ضابط NCA ECC-2:2024', 'سير عمل التحقق', 'لوحة CISO', 'تصنيف المجالات', 'النتائج الحرجة'] },
      { version: 'V2.0', status: 'active', items: ['جمع الأدلة تلقائياً', 'تقارير الفجوات', 'جدولة التدقيق', 'بوابة العملاء'] },
      { version: 'V3.0', status: 'planned', items: ['دعم ISO 27001, SOC 2', 'علاج بالذكاء الاصطناعي', 'تحليل اتجاه الامتثال'] },
    ],
  },
  {
    id: 'sahha',
    icon: '🏥',
    name: 'Sahha',
    nameAr: 'صحة',
    subtitle: 'Healthcare Claims Co-pilot',
    subtitleAr: 'طيار مطالبات الرعاية الصحية',
    status: 'live',
    statusLabel: 'يعمل على الإنتاج',
    progress: milestones[2] ? getMilestoneStats(milestones[2]).avgProgress : 0,
    color: '#448AFF',
    light: '#82B1FF',
    target: 'Q3 2026',
    tech: 'Node.js + SQLite + React',
    pricing: '٢٩٩ / ٥٩٩ / ١,٤٩٩ ريال/شهر',
    milestones: [
      { version: 'V1.0', status: 'done', items: ['٦٠ مطالبة برموز ICD-10-AM', 'تحليل AI للمطالبات', 'فحص توافق NPHIES', 'تصنيف الرفض', 'احتمالية الاسترداد'] },
      { version: 'V2.0', status: 'active', items: ['تقديم ذكي', 'إدارة الرفض', 'تحليلات دورة الإيرادات', 'مزامنة المزودين'] },
      { version: 'V3.0', status: 'planned', items: ['تحليلات تنبؤية', 'نمذجة سلوك الدافع', 'طعون تلقائية', 'دعم عيادات متعددة'] },
    ],
  },
  {
    id: 'dashboard',
    icon: '📊',
    name: 'Dashboard',
    nameAr: 'لوحة التحكم',
    subtitle: 'Executive Command Center',
    subtitleAr: 'مركز القيادة التنفيذي',
    status: 'live',
    statusLabel: 'يعمل',
    progress: milestones[3] ? getMilestoneStats(milestones[3]).avgProgress : 0,
    color: '#8B5CF6',
    light: '#C084FC',
    target: 'Q2 2026',
    tech: 'React + Vite + Tailwind',
    pricing: '—',
    milestones: [
      { version: 'V1.0', status: 'done', items: ['نظرة المنتج', 'مقاييس فورية', 'نظرة مالی', 'خارطة الطريق'] },
      { version: 'V2.0', status: 'active', items: ['تحليلات العملاء', 'تتبع الإيرادات', 'مراقبة نشاط الوكلاء', 'نظام تنبيهات'] },
      { version: 'V3.0', status: 'planned', items: ['دعم متعدد المستأجرين', 'تقارير مخصصة', 'تكاملات API', 'تطبيق جوال'] },
    ],
  },
  {
    id: 'labor',
    icon: '👷',
    name: 'Labor Portal',
    nameAr: 'بوابة العمالة',
    subtitle: 'B2B Labor Marketplace',
    subtitleAr: 'سوق تخصيص العمالة بين المؤسسات',
    status: 'live',
    statusLabel: 'Backend منتج — 60+ endpoint',
    progress: milestones[4] ? getMilestoneStats(milestones[4]).avgProgress : 0,
    color: '#FF6B35',
    light: '#FF9E6D',
    target: 'Flutter — 15 أبريل',
    tech: 'Node.js + SQLite + Flutter (قادم)',
    pricing: '١٩٩ / ٣٩٩ / ٩٩٩ ريال/شهر',
    milestones: [
      { version: 'Backend', status: 'active', agent: 'Lead Dev Agent', items: ['28+ جداول قاعدة بيانات', '70+ API endpoint', 'ربط الغرفة التجارية', 'Auth + OTP + JWT', 'العروض والطلبات', 'نظام المزايدة', 'إدارة العقود', 'لوحة الأدمن'] },
      { version: 'Flutter App', status: 'planned', agent: 'Lead Dev Agent', items: ['تسجيل الشركات (CR)', 'تصفح العروض', 'إنشاء الطلبات', 'طلبات خاصة + مزايدة', 'إدارة العقود', 'الإشعارات'] },
      { version: 'Web Admin', status: 'planned', agent: 'Lead Dev Agent', items: ['لوحة تحكم', 'إدارة المستخدمين', 'البيانات المرجعية', 'التقارير', 'نظام الدعم'] },
    ],
  },
  {
    id: 'website',
    icon: '🦅',
    name: 'Website',
    nameAr: 'موقع الشركة',
    subtitle: 'sanajim.com',
    subtitleAr: 'موقع سناجم الرسمي',
    status: 'live',
    statusLabel: 'مكتمل — مباشر',
    progress: milestones[5] ? getMilestoneStats(milestones[5]).avgProgress : 0,
    color: '#06B6D4',
    light: '#67E8F9',
    target: 'مكتمل',
    tech: 'React + RTL + Glass Morphism',
    pricing: '—',
    milestones: [
      { version: 'V1.0', status: 'done', items: ['العربية RTL', 'وضع داكن/فاتح', 'تأثير Glass Morphism', 'متجاوب مع الجوال'] },
    ],
  },
  {
    id: 'aiRacingGame',
    icon: '🏎️',
    name: 'AI Racing Game',
    nameAr: 'لعبة سباق بالذكاء الاصطناعي',
    subtitle: 'Mobile Racing Game',
    subtitleAr: 'لعبة سباق على الهاتف',
    status: 'planning',
    statusLabel: 'تخطيط — 60 يوم',
    progress: 5,
    color: '#FF4081',
    light: '#FF79B0',
    target: 'يونيو 2026',
    tech: 'Unity 6 LTS + URP + C#',
    pricing: 'مجاني + إعلانات + مشتريات داخل التطبيق',
    milestones: [
      { 
        version: 'المرحلة 1: النموذج الأولي (7 أيام)', 
        status: 'planned', 
        agent: 'Hermes Agent',
        items: [
          'إعداد مشروع Unity 6 LTS',
          'فيزياء السيارة الأساسية',
          'نظام التحكم (الميل + اللمس)',
          'خصوم ذكاء اصطناعي أساسيين',
          'نظام الأضرار الأساسي'
        ] 
      },
      { 
        version: 'المرحلة 2: ألفا (23 يوم)', 
        status: 'planned', 
        agent: 'Hermes Agent',
        items: [
          '3 أنماط لعبة (فوضى الطريق، سباق الدوائر، البقاء)',
          'واجهة مستخدم كاملة',
          'نظام التقدم (فتح السيارات، التطوير)',
          '5 سيارات مختلفة',
          '3 مسارات متميزة'
        ] 
      },
      { 
        version: 'المرحلة 3: بيتا (30 يوم)', 
        status: 'planned', 
        agent: 'Hermes Agent',
        items: [
          'تحسين الأداء للهاتف (60 إطار/ثانية)',
          'دمج الإعلانات والمشتريات',
          'اختبار وتحسين التجربة',
          'إعداد متجر التطبيقات',
          'الإطلاق على App Store و Google Play'
        ] 
      },
    ],
  },
]

// Financial assumptions
const FINANCIAL = {
  rafiq: { pricePerDriver: 99, currency: 'SAR', label: '٩٩ ريال/سائق/شهر' },
  muwafaq: { pricePerOrg: 4999, currency: 'SAR', label: '٤,٩٩٩ ريال/جهة/شهر' },
  sahha: { pricePerClinic: 2999, currency: 'SAR', label: '٢,٩٩٩ ريال/عيادة/شهر' },
  racing: { 
    revenueModel: 'Freemium + Ads + IAP', 
    estimatedARPDAU: 0.50, 
    currency: 'USD', 
    label: 'مجاني + إعلانات + مشتريات داخلية' 
  },
  breakEvenCustomers: 15,
  currentCustomers: 0,
  monthlyBurn: 0, // AI-only = zero burn
}

// Roadmap data — dynamically generated from milestones.js
const ROADMAP = milestones.map(m => {
  const stats = getMilestoneStats(m)
  return {
    product: m.product,
    milestone: m.name,
    progress: stats.avgProgress,
    dueDate: m.dueDate,
    tasks: { done: stats.done, total: stats.total }
  }
})

export default function Dashboard() {
  const { token, logout } = useAuth()
  const [healthData, setHealthData] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [realProjectsData, setRealProjectsData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeProduct, setActiveProduct] = useState('all')
  const [lastRefresh, setLastRefresh] = useState(null)
  const [projectsExpanded, setProjectsExpanded] = useState(true)
  const [expandedProject, setExpandedProject] = useState(null)
  const [expandedMilestone, setExpandedMilestone] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [detailsSection, setDetailsSection] = useState(null) // 'financial' | 'roadmap' | 'ecc' | 'analytics' | null

  const loadData = useCallback(async () => {
    try {
      const [health, dash, realProjects] = await Promise.all([
        fetchAllHealth(token),
        fetchAllDashboards(token),
        fetchRealProjects(),
      ])
      setHealthData(health)
      setDashboardData(dash)
      setRealProjectsData(realProjects)
      setLastRefresh(new Date())
    } catch {
      // keep previous
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 30000)
    return () => clearInterval(interval)
  }, [loadData])

  const navigate = useNavigate()
  const handleNavigate = (key) => {
    if (key === 'operations') return navigate('/operations')
    setActiveProduct(key)
  }

  // Compute display data from API responses
  const getProductData = (key) => {
    // First try to get data from dashboard APIs
    if (dashboardData?.[key] && dashboardData[key].status !== 'error') {
      return dashboardData[key]
    }
    
    // Fall back to real projects data from ops-api
    if (realProjectsData?.projects?.[key]) {
      const project = realProjectsData.projects[key]
      return {
        // Map real project data to expected format
        fpsPerformance: project.progress, // Use progress as FPS performance
        aiBotCount: 0, // Will be populated when AI bots are implemented
        gameModes: 3, // Planned: Highway Chaos, Circuit Race, Survival
        developmentProgress: project.progress,
        progress: project.progress,
        status: project.status,
        completedItems: project.completedItems,
        totalItems: project.totalItems,
      }
    }
    
    return {}
  }

  const getMetricValue = (data, metric) => {
    if (!data || typeof data !== 'object') return '--'
    
    // Special handling for racing game metrics
    if (metric.key === 'fpsPerformance') {
      return data.progress || 0 // Use progress as FPS performance
    }
    if (metric.key === 'aiBotCount') {
      return data.aiBotCount || 0
    }
    if (metric.key === 'gameModes') {
      return data.gameModes || 0
    }
    if (metric.key === 'developmentProgress') {
      return data.progress || 0
    }
    
    const val = data[metric.key]
    if (val === undefined || val === null) return '--'
    if (metric.format === 'percent') return `${val}%`
    if (metric.format === 'length' && Array.isArray(val)) return val.length
    return val
  }

  // Filter products for single-product view
  const visibleProducts = activeProduct === 'all'
    ? [...Object.keys(PRODUCTS), ...(realProjectsData?.projects ? Object.keys(realProjectsData.projects).filter(p => !Object.keys(PRODUCTS).includes(p)) : [])]
    : activeProduct === 'dashboard' || activeProduct === 'infra' || activeProduct === 'more'
      ? []
      : [activeProduct]

  const timeStr = lastRefresh
    ? lastRefresh.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })
    : '--:--'

  // Build activity items from dashboard data
  const getRecentActivity = () => {
    const items = []
    if (dashboardData?.muwafaq?.recentActivity?.length) {
      dashboardData.muwafaq.recentActivity.slice(0, 3).forEach(a => {
        items.push({ product: 'muwafaq', text: a.description || a.title || 'نشاط جديد', time: a.timestamp || a.created_at })
      })
    }
    // Add synthetic items from data
    if (dashboardData?.rafiq) {
      const r = dashboardData.rafiq
      if (r.draftMissions > 0) items.push({ product: 'rafiq', text: `${r.draftMissions} مسودة مهمة جديدة`, time: 'حديث' })
      if (r.driverCount > 0) items.push({ product: 'rafiq', text: `${r.driverCount} سائق مسجّل`, time: 'حديث' })
    }
    if (dashboardData?.muwafaq) {
      const m = dashboardData.muwafaq
      if (m.verifiedControls > 0) items.push({ product: 'muwafaq', text: `${m.verifiedControls} ضابط تم التحقق منه`, time: 'حديث' })
    }
    if (items.length === 0) {
      items.push(
        { product: 'rafiq', text: 'تم تطوير الإصدار 0.3 — APK جاهز', time: '٢٠٢٦-٠٤-١٣' },
        { product: 'muwafaq', text: 'تم نشر منصة موافق على الإنتاج', time: '٢٠٢٦-٠٤-١٣' },
        { product: 'sahha', text: 'تم تطوير واجهة المطالبات + تحليل AI', time: '٢٠٢٦-٠٤-١٣' },
      )
    }
    return items.slice(0, 6)
  }

  return (
    <div className="min-h-screen bg-void">
      <Sidebar
        activeProduct={activeProduct}
        onNavigate={handleNavigate}
        healthData={healthData}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Mobile hamburger */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="fixed top-4 right-4 z-50 p-2 rounded-lg bg-surface/90 backdrop-blur-sm border border-white/[0.06] text-[#94A3B8] hover:text-[#F1F5F9] lg:hidden"
        aria-label="فتح القائمة"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <main className="lg:mr-60 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 lg:p-6 pb-24 lg:pb-6 main-content-safe">

          {/* ─── Health Bar ─── */}
          <div className="mb-6">
            {loading ? <SkeletonHealthBar /> : <HealthBar />}
          </div>

          {/* ─── Delay Alerts ─── */}
          <AlertBanner />

          {/* ─── Company Header ─── */}
          <div className="mb-6 animate-fade-in-up" style={{ opacity: 0 }}>
            <div className="relative overflow-hidden rounded-[14px] bg-surface border border-white/[0.06] p-6">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-[#C084FC] via-[#8B5CF6] to-[#00BFA5] opacity-80" />
              <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-base border border-white/[0.06] flex items-center justify-center text-xl font-bold text-[#8B5CF6]">
                      س
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-[#F1F5F9]">{COMPANY.nameAr} 🦅</h1>
                      <p className="text-sm text-[#94A3B8]">{COMPANY.taglineAr}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <StatBox value={3} label="منتجات نشطة" color="#8B5CF6" />
                  <StatBox value={COMPANY.agents} label="وكلاء AI" color="#00BFA5" />
                  <StatBox value={0} label="موظفين بشريين" color="#64748B" />
                </div>
              </div>
            </div>
          </div>

          {/* ─── Always Visible: Project Status & Executive Overview ─── */}
          <div className="space-y-8 mb-8">
            {/* Real-time Project Status */}
            <RealTimeProjectStatus />
            
            {/* Executive Overview */}
            <ExecutiveOverview />
          </div>

          {/* ─── Product Portfolio ─── */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#F1F5F9] mb-4">📦 منتجاتنا</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {Object.entries(PRODUCTS).filter(([key]) => visibleProducts.includes(key)).map(([key, product], i) => {
                const data = getProductData(key)
                const health = healthData?.[key]
                return (
                  <div
                    key={key}
                    className="relative overflow-hidden rounded-[14px] bg-surface border border-white/[0.06] p-5 animate-fade-in-up hover:border-white/[0.10] transition-all duration-150"
                    style={{ animationDelay: `${i * 60}ms`, opacity: 0 }}
                  >
                    <div className="absolute top-0 inset-x-0 h-[2px] opacity-80"
                      style={{ background: `linear-gradient(90deg, ${product.color}, ${product.light})` }} />

                    {/* Product header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{product.icon}</span>
                        <div>
                          <h3 className="text-base font-bold text-[#F1F5F9]">{product.nameAr} <span className="text-sm font-normal text-[#64748B]">({product.name})</span></h3>
                        </div>
                      </div>
                      <HealthOrbSmall status={health?.status || realProjectsData?.projects?.[key]?.status || 'down'} />
                    </div>

                    <p className="text-xs text-[#94A3B8] mb-4 leading-relaxed">{product.descriptionAr}</p>
                    <p className="text-[10px] text-[#64748B] mb-4">{product.statusAr}</p>

                    {/* Metrics grid */}
                    <div className="grid grid-cols-2 gap-2">
                      {product.metrics.map((metric, j) => {
                        const val = getMetricValue(data, metric)
                        return (
                          <div key={metric.key} className="rounded-[10px] bg-base border border-white/[0.04] p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-xs">{metric.icon}</span>
                              <span className="text-[10px] font-medium text-[#64748B]">{metric.label}</span>
                            </div>
                            <p className="text-xl font-extrabold tabular-nums" style={{ color: product.color }}>
                              {typeof val === 'number' ? <AnimatedNumber value={val} duration={500} /> : val}
                            </p>
                          </div>
                        )
                      })}
                    </div>

                    {/* Domain breakdown for Muwafaq */}
                    {key === 'muwafaq' && data?.domainBreakdown && typeof data.domainBreakdown === 'object' && (
                      <div className="mt-3 space-y-2">
                        <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">توزيع المجالات</p>
                        {safeEntries(data.domainBreakdown).map(([domain, stats]) => (
                          <div key={domain}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-[#94A3B8]">{domain}</span>
                              <span className="text-[10px] tabular-nums text-[#64748B]">{stats?.verified || 0}/{stats?.total || 0}</span>
                            </div>
                            <GlowingTrackBar
                              progress={stats?.total > 0 ? Math.round(((stats?.verified || 0) / stats.total) * 100) : 0}
                              color={product.color}
                              lightColor={product.light}
                              height="h-1.5"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Severity distribution for Muwafaq */}
                    {key === 'muwafaq' && data?.severityDistribution && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-semibold text-[#64748B]">الخطورة:</span>
                        <SeverityBadge label="حرج" count={data.severityDistribution.critical} color="#EF4444" />
                        <SeverityBadge label="عالي" count={data.severityDistribution.high} color="#F59E0B" />
                        <SeverityBadge label="متوسط" count={data.severityDistribution.medium} color="#3B82F6" />
                        <SeverityBadge label="منخفض" count={data.severityDistribution.low} color="#22C55E" />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* ─── Collapsible: Additional Details ─── */}
          {/* ═══════════════════════════════════════════════════════ */}
          <div className="mb-6">
            <button
              onClick={() => setDetailsSection(detailsSection ? null : 'open')}
              className="w-full flex items-center justify-between p-4 rounded-[14px] bg-surface border border-white/[0.06] hover:border-white/[0.12] transition-all group mb-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[8px] bg-[#8B5CF6]/10 flex items-center justify-center">
                  <span className="text-lg">📋</span>
                </div>
                <div className="text-right">
                  <h3 className="text-sm font-bold text-[#F1F5F9]">تفاصيل إضافية</h3>
                  <p className="text-[10px] text-[#64748B]">المالية • خارطة الطريق • التحكم • التحليلات</p>
                </div>
              </div>
              <svg
                className={`w-5 h-5 text-[#64748B] transition-transform duration-200 group-hover:text-[#C084FC] ${detailsSection ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {detailsSection && (
              <div className="space-y-8 animate-fade-in-up">

                {/* ─── ECC Controls ─── */}
                <MuwafaqECCControls />

                {/* ─── Enhanced Analytics ─── */}
                <EnhancedAnalytics />

          {/* ─── Financial Overview ─── */}
          <div className="mb-6 animate-fade-in-up" style={{ opacity: 0, animationDelay: '120ms' }}>
            <h2 className="text-lg font-bold text-[#F1F5F9] mb-4">💰 النظرة المالية</h2>
            <div className="relative overflow-hidden rounded-[14px] bg-surface border border-white/[0.06] p-5">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-[#F59E0B] to-[#FCD34D] opacity-80" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <FinancialCard
                  label="العملاء الحاليين"
                  value={FINANCIAL.currentCustomers}
                  sub="Pre-revenue stage"
                  color="#64748B"
                />
                <FinancialCard
                  label="نقطة التعادل"
                  value={FINANCIAL.breakEvenCustomers}
                  sub="عملاء للوصول للتعادل"
                  color="#F59E0B"
                />
                <FinancialCard
                  label="الاحتراق الشهري"
                  value="٠ ريال"
                  sub="AI-only = تكلفة صفر"
                  color="#22C55E"
                />
                <FinancialCard
                  label="الإيراد المحتمل"
                  value="١٥٠ ألف+"
                  sub="عند ١٥ عميل/شهر"
                  color="#8B5CF6"
                />
              </div>

              {/* Pricing breakdown */}
              <div className="mt-4 pt-4 border-t border-white/[0.06]">
                <p className="text-xs font-semibold text-[#64748B] mb-3">أسعار الاشتراك:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {Object.entries(FINANCIAL).filter(([k]) => ['rafiq', 'muwafaq', 'sahha'].includes(k)).map(([key, pricing]) => {
                    const product = PRODUCTS[key]
                    return (
                      <div key={key} className="flex items-center gap-2 rounded-[10px] bg-base border border-white/[0.04] p-3">
                        <span className="text-lg">{product.icon}</span>
                        <div>
                          <p className="text-xs font-medium text-[#F1F5F9]">{product.nameAr}</p>
                          <p className="text-[10px] text-[#94A3B8]">{pricing.label}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Progress to break-even */}
              <div className="mt-4 pt-4 border-t border-white/[0.06]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#64748B]">التقدم نحو نقطة التعادل</span>
                  <span className="text-sm font-bold tabular-nums text-[#F59E0B]">
                    {FINANCIAL.currentCustomers}/{FINANCIAL.breakEvenCustomers} عملاء
                  </span>
                </div>
                <GlowingTrackBar
                  progress={Math.round((FINANCIAL.currentCustomers / FINANCIAL.breakEvenCustomers) * 100)}
                  color="#F59E0B"
                  lightColor="#FCD34D"
                  height="h-3"
                />
              </div>
            </div>
          </div>

          {/* ─── Roadmap & Activity Row ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

            {/* Roadmap */}
            <div className="animate-fade-in-up" style={{ opacity: 0, animationDelay: '160ms' }}>
              <h2 className="text-lg font-bold text-[#F1F5F9] mb-4">🗺️ خارطة الطريق</h2>
              <div className="relative overflow-hidden rounded-[14px] bg-surface border border-white/[0.06] p-5">
                <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-[#8B5CF6] to-[#C084FC] opacity-80" />
                <div className="space-y-4">
                  {ROADMAP.map((item, i) => {
                    const product = PRODUCTS[item.product] || { icon: '📊', nameAr: 'لوحة التحكم', color: '#8B5CF6', light: '#C084FC' }
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-lg">{product.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-[#F1F5F9] truncate">{item.milestone}</span>
                            <span className="text-xs tabular-nums" style={{ color: product.color }}>{item.progress}%</span>
                          </div>
                          <GlowingTrackBar
                            progress={item.progress}
                            color={product.color}
                            lightColor={product.light}
                            height="h-2"
                          />
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-[#64748B]">{item.tasks.done}/{item.tasks.total} مهام</span>
                            <span className="text-[10px] text-[#64748B]">{item.dueDate}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Activity Feed */}
            <div className="animate-fade-in-up" style={{ opacity: 0, animationDelay: '200ms' }}>
              <h2 className="text-lg font-bold text-[#F1F5F9] mb-4">⚡ آخر النشاطات</h2>
              <div className="relative overflow-hidden rounded-[14px] bg-surface border border-white/[0.06] p-5">
                <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-[#00BFA5] to-[#00E5CC] opacity-80" />
                <div className="space-y-3">
                  {getRecentActivity().map((item, i) => {
                    const product = PRODUCTS[item.product] || { icon: '📊', color: '#8B5CF6' }
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-[10px] bg-base border border-white/[0.04]">
                        <span className="text-base mt-0.5">{product.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#F1F5F9]">{item.text}</p>
                          <p className="text-[10px] text-[#64748B] mt-0.5">{item.time}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* ─── Strategic Projects ─── */}
          <div className="mb-6 animate-fade-in-up" style={{ opacity: 0, animationDelay: '220ms' }}>
            <button
              onClick={() => setProjectsExpanded(!projectsExpanded)}
              className="flex items-center gap-2 mb-4 group"
            >
              <h2 className="text-lg font-bold text-[#F1F5F9] group-hover:text-[#C084FC] transition-colors">🏗️ المشاريع الاستراتيجية</h2>
              <svg
                className={`w-4 h-4 text-[#64748B] transition-transform duration-200 ${projectsExpanded ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              <span className="text-[10px] text-[#64748B] mr-1">({STRATEGIC_PROJECTS.length} مشاريع)</span>
            </button>

            {projectsExpanded && (
              <div className="space-y-4">
                {STRATEGIC_PROJECTS.map((project, i) => {
                  const isExpanded = expandedProject === project.id
                  const statusColors = {
                    'live': { bg: 'rgba(34,197,94,0.1)', text: '#22C55E', border: 'rgba(34,197,94,0.2)' },
                    'in-dev': { bg: 'rgba(245,158,11,0.1)', text: '#F59E0B', border: 'rgba(245,158,11,0.2)' },
                    'planned': { bg: 'rgba(100,116,139,0.1)', text: '#94A3B8', border: 'rgba(100,116,139,0.2)' },
                  }
                  const sc = statusColors[project.status] || statusColors.planned
                  const milestoneIcons = { done: '✅', active: '🔄', planned: '⏳' }

                  return (
                    <div
                      key={project.id}
                      className="relative overflow-hidden rounded-[14px] bg-surface border border-white/[0.06] hover:border-white/[0.10] transition-all duration-150"
                    >
                      <div
                        className="absolute top-0 inset-x-0 h-[2px] opacity-80"
                        style={{ background: `linear-gradient(90deg, ${project.color}, ${project.light})` }}
                      />

                      {/* Clickable header */}
                      <button
                        onClick={() => { setExpandedProject(isExpanded ? null : project.id); setExpandedMilestone(null) }}
                        className="w-full text-right p-5 focus:outline-none"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <span className="text-2xl mt-0.5">{project.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-base font-bold text-[#F1F5F9]">
                                  {project.nameAr} <span className="text-sm font-normal text-[#64748B]">({project.name})</span>
                                </h3>
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                  style={{ backgroundColor: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}
                                >
                                  {project.statusLabel}
                                </span>
                              </div>
                              <p className="text-xs text-[#94A3B8] mt-0.5">{project.subtitleAr} — {project.subtitle}</p>

                              {/* Progress bar inline */}
                              <div className="flex items-center gap-3 mt-3">
                                <div className="flex-1">
                                  <GlowingTrackBar
                                    progress={project.progress}
                                    color={project.color}
                                    lightColor={project.light}
                                    height="h-2.5"
                                  />
                                </div>
                                <span className="text-sm font-bold tabular-nums" style={{ color: project.color }}>{project.progress}%</span>
                              </div>

                              {/* Quick info row */}
                              <div className="flex items-center gap-4 mt-2 flex-wrap">
                                <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                                  <span>🎯</span> {project.target}
                                </span>
                                <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                                  <span>⚙️</span> {project.tech}
                                </span>
                                {project.pricing !== '—' && (
                                  <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                                    <span>💰</span> {project.pricing}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Expand chevron */}
                          <svg
                            className={`w-5 h-5 text-[#64748B] transition-transform duration-200 flex-shrink-0 mt-1 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/* Expanded milestones — DRILL DOWN: Level 2 → Level 3 */}
                      {isExpanded && (() => {
                        const projectMilestone = milestones.find(m => m.product === project.id)
                        const allTasks = projectMilestone?.tasks || []
                        
                        // Map tasks to version milestones by parsing task ID prefix (e.g., '1.1' → group 1)
                        const versionGroups = (project.milestones || []).map((ms, j) => {
                          const versionNum = j + 1
                          // Tasks whose integer part of ID matches version number
                          const versionTasks = allTasks.filter(t => {
                            const parts = String(t.id).split('.')
                            const taskGroup = parseInt(parts[0], 10)
                            return taskGroup === versionNum || (isNaN(taskGroup) && j === 0)
                          })
                          // If no ID-based match, distribute evenly as fallback
                          const finalTasks = versionTasks.length > 0 ? versionTasks : (() => {
                            const perVersion = Math.ceil(allTasks.length / project.milestones.length)
                            return allTasks.slice(j * perVersion, Math.min((j + 1) * perVersion, allTasks.length))
                          })()
                          const stats = {
                            total: finalTasks.length,
                            done: finalTasks.filter(t => t.status === 'done').length,
                            inProgress: finalTasks.filter(t => t.status === 'in_progress').length,
                            pending: finalTasks.filter(t => t.status === 'pending').length,
                            overdue: finalTasks.filter(t => t.status === 'overdue').length,
                            avgProgress: finalTasks.length > 0 ? Math.round(finalTasks.reduce((s, t) => s + t.progress, 0) / finalTasks.length) : 0
                          }
                          return { ...ms, tasks: finalTasks, stats, key: `${project.id}-${j}` }
                        })
                        
                        return (
                        <div className="px-5 pb-5 border-t border-white/[0.06] pt-4">
                          <div className="space-y-3">
                            {versionGroups.map((vg) => {
                              const isVersionExpanded = expandedMilestone === vg.key
                              return (
                                <div key={vg.key} className="rounded-[12px] bg-base border border-white/[0.04] overflow-hidden">
                                  {/* Level 2: Version milestone header — clickable */}
                                  <button
                                    onClick={() => setExpandedMilestone(isVersionExpanded ? null : vg.key)}
                                    className="w-full text-right p-3 hover:bg-white/[0.02] transition-colors focus:outline-none"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs">{milestoneIcons[vg.status]}</span>
                                        <span className="text-xs font-bold text-[#F1F5F9]">{vg.version}</span>
                                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                          vg.status === 'done' ? 'bg-green-500/10 text-green-400' :
                                          vg.status === 'active' ? 'bg-amber-500/10 text-amber-400' :
                                          'bg-slate-500/10 text-slate-400'
                                        }`}>
                                          {vg.status === 'done' ? 'مكتمل' : vg.status === 'active' ? 'جاري' : 'مخطط'}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-1.5">
                                          <div className="w-16 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                                            <div
                                              className="h-full rounded-full"
                                              style={{ width: `${vg.stats.avgProgress}%`, backgroundColor: project.color }}
                                            />
                                          </div>
                                          <span className="text-[10px] font-bold" style={{ color: project.color }}>{vg.stats.avgProgress}%</span>
                                        </div>
                                        <span className="text-[10px] text-[#94A3B8]">{vg.stats.done}/{vg.stats.total} مهام</span>
                                        <svg className={`w-4 h-4 text-[#64748B] transition-transform duration-200 ${isVersionExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </div>
                                    </div>
                                  </button>
                                  
                                  {/* Level 3: Detailed tasks — shown when version milestone is expanded */}
                                  {isVersionExpanded && vg.tasks.length > 0 && (
                                    <div className="px-3 pb-3 space-y-1.5">
                                      {vg.tasks.map((task) => {
                                        const sc = task.status === 'done' 
                                          ? { bg: 'bg-green-500/10', text: 'text-green-400', label: '✅ مكتمل', dot: 'bg-green-400' }
                                          : task.status === 'in_progress'
                                          ? { bg: 'bg-amber-500/10', text: 'text-amber-400', label: '🔄 جاري', dot: 'bg-amber-400' }
                                          : { bg: 'bg-slate-500/10', text: 'text-slate-400', label: '⏳ متبقي', dot: 'bg-slate-400' }
                                        return (
                                          <div key={task.id} className="flex items-start gap-2.5 p-2.5 bg-white/[0.02] rounded-lg border border-white/[0.02]">
                                            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${sc.dot} ${task.status === 'in_progress' ? 'animate-pulse' : ''}`} />
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center justify-between gap-2">
                                                <p className="text-[12px] font-semibold text-[#F1F5F9] truncate">{task.name}</p>
                                                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium flex-shrink-0 ${sc.bg} ${sc.text}`}>{sc.label}</span>
                                              </div>
                                              <div className="flex items-center gap-2.5 mt-1 flex-wrap">
                                                {task.assignee && task.assignee !== '—' && <span className="text-[10px] text-[#94A3B8]">🤖 {task.assignee}</span>}
                                                {task.dueDate && <span className="text-[10px] text-[#94A3B8]">📅 {task.dueDate}</span>}
                                                {(() => { const label = getDaysRemainingLabel(task.dueDate); return label ? <span className={`text-[10px] font-bold ${daysRemaining(task.dueDate) < 0 ? 'text-red-400' : daysRemaining(task.dueDate) <= 1 ? 'text-amber-400' : 'text-[#94A3B8]'}`}>{label}</span> : null })()}
                                                <span className="text-[10px] font-bold" style={{ color: project.color }}>{task.progress}%</span>
                                              </div>
                                              {task.notes && <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">{task.notes}</p>}
                                              <div className="w-full h-1 bg-white/[0.04] rounded-full overflow-hidden mt-1.5">
                                                <div className="h-full rounded-full" style={{ width: `${task.progress}%`, backgroundColor: project.color }} />
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Show items list when NOT expanded (original view) */}
                                  {!isVersionExpanded && (
                                    <div className="px-3 pb-3">
                                      <ul className="space-y-1">
                                        {(vg.items || []).map((item, k) => (
                                          <li key={k} className="text-[11px] text-[#94A3B8] flex items-start gap-1.5">
                                            <span className="mt-1 w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />
                                            <span>{item}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                        )
                      })()}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* ─── Critical Findings (Muwafaq) ─── */}
          {dashboardData?.muwafaq?.criticalFindings?.length > 0 && (
            <div className="mb-6 animate-fade-in-up" style={{ opacity: 0, animationDelay: '240ms' }}>
              <h2 className="text-lg font-bold text-[#F1F5F9] mb-4">🚨 نتائج حرجة — موافق</h2>
              <div className="relative overflow-hidden rounded-[14px] bg-surface border border-red-500/15 p-5">
                <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-[#EF4444] to-[#F87171] opacity-80" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {dashboardData?.muwafaq?.criticalFindings?.slice(0, 10).map((finding, i) => (
                    <div key={finding?.id || i} className="flex items-center gap-2 p-2.5 rounded-[8px] bg-base border border-white/[0.04]">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-subtle-alarm-dot flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[#F1F5F9] truncate">{finding?.title_ar || 'نتيجة حرجة'}</p>
                        <p className="text-[10px] text-[#64748B]">{finding?.code || 'N/A'} — {finding?.domain || 'عام'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Cost Panel ─── */}
          <div className="mb-6 animate-fade-in-up" style={{ opacity: 0, animationDelay: '260ms' }}>
            <CostPanel />
          </div>

          {/* ─── Detailed Milestones ─── */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">📋 تفاصيل المشاريع — Milestones</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#94A3B8]">
                  {getOverallStats().done}/{getOverallStats().total} مكتمل
                </span>
                <span className="text-xs font-bold text-white">
                  {getOverallStats().avgProgress}%
                </span>
              </div>
            </div>
            <div className="space-y-4">
              {milestones.map(milestone => {
                const stats = getMilestoneStats(milestone)
                const pc = productConfigs[milestone.product]
                return (
                  <div key={milestone.id} className="bg-surface rounded-2xl border border-white/[0.06] overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-white/[0.06]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{milestone.icon}</span>
                          <div>
                            <h3 className="text-sm font-bold text-white">{milestone.name}</h3>
                            <p className="text-[11px] text-[#94A3B8]">{milestone.dueDate}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-lg font-extrabold text-white">{stats.avgProgress}%</p>
                            <p className="text-[10px] text-[#94A3B8]">التقدم</p>
                          </div>
                          <div className="flex gap-1.5">
                            <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-green-500/10 text-green-400">{stats.done} مكتمل</span>
                            {stats.inProgress > 0 && <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-400">{stats.inProgress} جاري</span>}
                            {stats.pending > 0 && <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-slate-500/10 text-slate-400">{stats.pending} متبقي</span>}
                          </div>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3">
                        <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${stats.avgProgress}%`,
                              background: pc?.gradient || 'linear-gradient(90deg, #8B5CF6, #C084FC)'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Tasks */}
                    <div className="p-4 space-y-2">
                      {milestone.tasks.map(task => {
                        const statusColors = {
                          done: { bg: 'bg-green-500/10', text: 'text-green-400', label: '✅ مكتمل', dot: 'bg-green-400' },
                          in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: '🔄 جاري', dot: 'bg-amber-400' },
                          pending: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: '⏳ متبقي', dot: 'bg-slate-400' },
                          overdue: { bg: 'bg-red-500/10', text: 'text-red-400', label: '🔴 متأخر', dot: 'bg-red-400' },
                        }
                        const sc = statusColors[task.status] || statusColors.pending
                        return (
                          <div key={task.id} className="flex items-start gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/[0.03]">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sc.dot} ${task.status === 'in_progress' ? 'animate-pulse' : ''}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[13px] font-semibold text-white truncate">{task.name}</p>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${sc.bg} ${sc.text}`}>{sc.label}</span>
                              </div>
                              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                {task.assignee && task.assignee !== '—' && <span className="text-[11px] text-[#94A3B8]">🤖 {task.assignee}</span>}
                                {task.dueDate && <span className="text-[11px] text-[#94A3B8]">📅 {task.dueDate}</span>}
                                {(() => { const label = getDaysRemainingLabel(task.dueDate); return label ? <span className={`text-[11px] font-bold ${daysRemaining(task.dueDate) < 0 ? 'text-red-400' : daysRemaining(task.dueDate) <= 1 ? 'text-amber-400' : 'text-[#94A3B8]'}`}>{label}</span> : null })()}
                                <span className="text-[11px] font-bold" style={{ color: pc?.primary || '#8B5CF6' }}>{task.progress}%</span>
                              </div>
                              {task.notes && <p className="text-[11px] text-[#94A3B8] mt-1.5 leading-relaxed">{task.notes}</p>}
                              {/* Task progress bar */}
                              <div className="w-full h-1 bg-white/[0.04] rounded-full overflow-hidden mt-2">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${task.progress}%`,
                                    backgroundColor: pc?.primary || '#8B5CF6'
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

              </div>
            )}
          </div>

          {/* ─── Footer ─── */}
          <div className="text-center py-4">
            <p className="text-[10px] text-[#475569]">آخر تحديث: {timeStr} — يتم التحديث كل ٣٠ ثانية</p>
            <p className="text-[10px] text-[#475569] mt-1">© 2026 Sanajim Technologies — جميع الحقوق محفوظة</p>
          </div>

        </div>
      </main>

      <MobileNav activeProduct={activeProduct} onNavigate={handleNavigate} />
    </div>
  )
}

// ─── Helper Components ───

function StatBox({ value, label, color }) {
  return (
    <div className="rounded-[10px] bg-base border border-white/[0.04] p-3 text-center min-w-[70px]">
      <p className="text-2xl font-extrabold tabular-nums" style={{ color }}>
        <AnimatedNumber value={value} duration={500} />
      </p>
      <p className="text-[10px] font-medium text-[#64748B] mt-0.5">{label}</p>
    </div>
  )
}

function FinancialCard({ label, value, sub, color }) {
  return (
    <div className="rounded-[10px] bg-base border border-white/[0.04] p-4">
      <p className="text-[10px] font-medium text-[#64748B] mb-1">{label}</p>
      <p className="text-xl font-extrabold tabular-nums" style={{ color }}>
        {typeof value === 'number' ? <AnimatedNumber value={value} duration={500} /> : value}
      </p>
      <p className="text-[10px] text-[#94A3B8] mt-0.5">{sub}</p>
    </div>
  )
}

function HealthOrbSmall({ status }) {
  const colors = { 
    healthy: '#22C55E', 
    degraded: '#F59E0B', 
    down: '#EF4444',
    built: '#3B82F6',
    planned: '#8B5CF6',
    scaffold: '#EC4899'
  }
  const labels = { 
    healthy: 'يعمل', 
    degraded: 'بطيء', 
    down: 'متوقف',
    built: 'مبني',
    planned: 'مخطط',
    scaffold: 'هيكل'
  }
  const color = colors[status] || colors.down
  const label = labels[status] || labels.down
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <div className="relative">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
        {(status === 'healthy' || status === 'built') && (
          <div className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-health-glow" style={{ backgroundColor: color, opacity: 0.35 }} />
        )}
      </div>
      <span className="text-[10px] font-medium" style={{ color }}>{label}</span>
    </div>
  )
}

function SeverityBadge({ label, count, color }) {
  if (!count) return null
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border"
      style={{ color, borderColor: `${color}30`, backgroundColor: `${color}10` }}>
      {count} {label}
    </span>
  )
}
